import type { FC } from 'react';
import React, { Fragment } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import Textfield from '@atlaskit/textfield';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import SelectClearIcon from '@atlaskit/icon/core/cross-circle';
import SearchIcon from '@atlaskit/icon/core/search';
import { ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';

import { ContentDescription } from './ContentDescription';
import { SpaceDescription } from './SpaceDescription';
import type { ContentAccessDetailsQuery as ContentAccessDetailsQueryType } from './__types__/ContentAccessDetailsQuery';
import type { SpaceAccessDetailsQuery as SpaceAccessDetailsQueryType } from './__types__/SpaceAccessDetailsQuery';

type AccessDetailsDialogHeaderProps = {
	appliedFilterText: string;
	filterText: string;
	setFilterText: (_: string) => void;
	onClose: () => void;
	spaceInfo?: SpaceAccessDetailsQueryType;
	contentInfo?: ContentAccessDetailsQueryType;
	hasInheritedRestrictions: boolean;
	isLoading: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderLayout = styled.div({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderContainer = styled.div({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'row',
	alignItems: 'center',
	paddingBottom: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled.div({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'row',
	alignItems: 'center',
	paddingBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		height: '24px',
		width: '24px',
	},
});

const titleTextStyles = xcss({
	paddingLeft: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchIconWrapper = styled.div({
	padding: token('space.050'),
	display: 'inline-flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalHeaderWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-testid="space-access-details.drilldown--header"]': {
		paddingBottom: token('space.200'),
	},
});

const i18n = defineMessages({
	clearFilterLabel: {
		id: 'space-access-details.access-details-dialog.clear-filter',
		defaultMessage: 'Clear selection',
		description: 'Label for button that clears text from the filter field',
	},
	countOfIndividualsSuffix: {
		id: 'space-access-details.access-details-dialog.count-of-groups-and-individuals-suffix',
		defaultMessage: 'and {countOfIndividuals, plural, one {# individual} other {# individuals}}',
		description:
			'Latter part of placeholder text when there are both individuals and groups, to be concatenated with space-access-details.access-details-dialog.count-of-groups',
	},
	countOfGroups: {
		id: 'space-access-details.access-details-dialog.count-of-groups',
		defaultMessage: 'Search {countOfGroups, plural, one {# group} other {# groups}}',
		description: 'Placeholder text when there are only groups',
	},
	countOfIndividuals: {
		id: 'space-access-details.access-details-dialog.count-of-individuals',
		defaultMessage: 'Search {countOfIndividuals, plural, one {# individual} other {# individuals}}',
		description: 'Placeholder text when there are only individuals',
	},
});

export const AccessDetailsDialogHeader: FC<AccessDetailsDialogHeaderProps> = ({
	hasInheritedRestrictions,
	onClose,
	filterText,
	setFilterText,
	spaceInfo,
	contentInfo,
	isLoading,
	appliedFilterText,
}) => {
	const intl = useIntl();
	const handleFilterTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilterText(event.currentTarget.value);
	};

	let countOfIndividuals;
	let countOfGroups;
	if (hasInheritedRestrictions) {
		countOfIndividuals =
			contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.userCount;
		countOfGroups =
			contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.groupCount;
	} else {
		countOfIndividuals = spaceInfo?.spacePermissions?.subjectsWithPermissions?.userCount || 0;
		countOfGroups = spaceInfo?.spacePermissions?.subjectsWithPermissions?.groupCount || 0;
	}

	let placeholder;
	// Input cleared but filter is still there - prevent flashing of placeholder
	if (isLoading || (appliedFilterText && !filterText)) {
		placeholder = '';
	} else {
		if (countOfIndividuals && countOfGroups) {
			placeholder = `${intl.formatMessage(i18n.countOfGroups, {
				countOfGroups,
			})} ${intl.formatMessage(i18n.countOfIndividualsSuffix, {
				countOfIndividuals,
			})}`;
		} else if (countOfGroups) {
			placeholder = intl.formatMessage(i18n.countOfGroups, {
				countOfGroups,
			});
		} else {
			placeholder = intl.formatMessage(i18n.countOfIndividuals, {
				countOfIndividuals,
			});
		}
	}

	return (
		<ModalHeaderWrapper>
			<ModalHeader>
				<HeaderLayout>
					<ModalTitle>
						<TitleContainer>
							<Button
								aria-label="Back"
								appearance="subtle"
								iconBefore={<ArrowLeftIcon label="" />}
								onClick={onClose}
							/>
							<Box xcss={titleTextStyles}>
								{hasInheritedRestrictions ? (
									<FormattedMessage
										id="space-access-details.access-details-dialog.restricting-page-header"
										defaultMessage="Who has access to the parent item"
										description="Dialog title for the case when page audience is limited by a parent page/content"
									/>
								) : (
									<FormattedMessage
										id="space-access-details.access-details-dialog.restricting-space-header"
										defaultMessage="Who has access to this space"
										description="Dialog title for the case when page audience is defined by space"
									/>
								)}
							</Box>
						</TitleContainer>
					</ModalTitle>
					<HeaderContainer>
						{!hasInheritedRestrictions ? (
							<SpaceDescription spaceInfo={spaceInfo} isLoading={isLoading} />
						) : (
							<ContentDescription contentInfo={contentInfo} isLoading={isLoading} />
						)}
					</HeaderContainer>
					<Textfield
						aria-label={placeholder}
						autoFocus
						placeholder={placeholder}
						value={filterText}
						onChange={handleFilterTextChange}
						elemBeforeInput={
							<SearchIconWrapper>
								<SearchIcon spacing="spacious" color={token('color.icon.subtle')} label="" />
							</SearchIconWrapper>
						}
						elemAfterInput={
							<Fragment>
								{Boolean(filterText) && (
									<Button
										appearance="subtle"
										aria-label={intl.formatMessage(i18n.clearFilterLabel)}
										iconBefore={<SelectClearIcon label="" color={token('color.icon.subtle')} />}
										onClick={() => setFilterText('')}
									/>
								)}
							</Fragment>
						}
					/>
				</HeaderLayout>
			</ModalHeader>
		</ModalHeaderWrapper>
	);
};
